/**
 * A wall's length typed on its dimension label, as the app sets it (`WallController.SetLength` →
 * `WallSizeHandler.TrySetLength`): one end moves along the wall by the difference, the other stays. Which end moves
 * follows the app's `GetFreeZoneToMove`, as far as the web's plan knows its reasons: not the end that runs on straight
 * into another wall the way it would grow, then the end with fewer walls meeting it, else the wall's end; when that end
 * cannot move, the other one. A wall meeting the moving end at a right angle moves with it, parallel, so the corner
 * stays square (`WallDragHandler`); a wall at another angle stretches. Doors and windows stay where they stood.
 */
import type { Level, Vec2, Wall } from '../core/hp/types';
import { moveCorners, samePoint, type EditResult } from '../core/geometry/edit';
import { keepOpeningsInPlace } from './roomSize';
import { t } from '../i18n/I18n';

/** The app's limits (`GetMinLength`, `SetLength`'s 10.5 cm floor, `ModelSettings.WallMaxLength`). */
export const WALL_LENGTH_LIMITS = { min: .105, max: 150 } as const;
/** A neighbour within 3° of a right angle moves parallel (`RightAngleTolerance`). */
const RIGHT_ANGLE = 3 * Math.PI / 180;

const direction = (wall: Wall): Vec2 => {
  const dx = wall.end.x - wall.start.x, dz = wall.end.z - wall.start.z, length = Math.hypot(dx, dz) || 1;
  return { x: dx / length, z: dz / length };
};
const meeting = (level: Level, wall: Wall, point: Vec2) =>
  level.walls.filter(other => other.id !== wall.id && (samePoint(other.start, point) || samePoint(other.end, point)));

/** The ends in the order the app tries them. */
function endOrder(level: Level, wall: Wall): Array<'start' | 'end'> {
  const along = direction(wall);
  const score = (end: 'start' | 'end') => {
    const point = wall[end], outward = end === 'end' ? along : { x: -along.x, z: -along.z };
    const others = meeting(level, wall, point);
    // Growing would run into a wall that goes on straight from this end.
    const straight = others.some(other => {
      const far = samePoint(other.start, point) ? other.end : other.start;
      const dx = far.x - point.x, dz = far.z - point.z, length = Math.hypot(dx, dz) || 1;
      return (dx / length) * outward.x + (dz / length) * outward.z > Math.cos(5 * Math.PI / 180);
    });
    return (straight ? 100 : 0) + others.length;
  };
  return score('end') <= score('start') ? ['end', 'start'] : ['start', 'end'];
}

/** The corners that move to put `end` of the wall `delta` further along it, with any square neighbour carried. */
function moves(level: Level, wall: Wall, end: 'start' | 'end', delta: number): Array<{ from: Vec2; to: Vec2 }> {
  const along = direction(wall), sign = end === 'end' ? 1 : -1, shift = { x: along.x * delta * sign, z: along.z * delta * sign };
  const point = wall[end], result = [{ from: point, to: { x: point.x + shift.x, z: point.z + shift.z } }];
  for (const other of meeting(level, wall, point)) {
    const far = samePoint(other.start, point) ? other.end : other.start;
    const dx = far.x - point.x, dz = far.z - point.z, length = Math.hypot(dx, dz);
    if (length < 1e-6 || Math.abs(Math.abs(Math.acos(Math.min(1, Math.abs((dx * along.x + dz * along.z) / length)))) - Math.PI / 2) > RIGHT_ANGLE) continue;
    if (!result.some(move => samePoint(move.from, far))) result.push({ from: far, to: { x: far.x + shift.x, z: far.z + shift.z } });
  }
  return result;
}

export function setWallLength(level: Level, wallId: string, length: number): EditResult {
  const wall = level.walls.find(value => value.id === wallId);
  if (!wall) return { error: t('#GeometryWallNotFound') };
  if (!Number.isFinite(length) || length < WALL_LENGTH_LIMITS.min || length > WALL_LENGTH_LIMITS.max)
    return { error: t('#WallLengthRange', String(WALL_LENGTH_LIMITS.min), String(WALL_LENGTH_LIMITS.max)) };
  const delta = length - wall.length;
  if (Math.abs(delta) < 5e-4) return { level };
  let error = t('#WallLengthBlocked');
  for (const end of endOrder(level, wall)) {
    const result = moveCorners(level, moves(level, wall, end, delta));
    if (!result.level) { error = result.error ?? error; continue; }
    const after = result.level.walls.find(value => value.id === wallId);
    if (after && Math.abs(after.length - length) < .005) return { level: keepOpeningsInPlace(result.level, level) };
  }
  return { error };
}
